import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  MapPin, Plus, Edit, Trash2, Search, Building2, Warehouse, Home,
  Upload, Download, Map, X, Loader2, Check, AlertTriangle, Navigation
} from "lucide-react";
import { toast } from "sonner";

const LOCATION_TYPES = [
  { value: "circle_office_warehouse", label: "Circle Office & Warehouse", icon: Building2, color: "bg-blue-500" },
  { value: "hq", label: "Head Quarters", icon: Home, color: "bg-purple-500" },
  { value: "depot", label: "Depot", icon: Warehouse, color: "bg-green-500" },
  { value: "sub_depot", label: "Sub Depot", icon: MapPin, color: "bg-orange-500" }
];

const TYPE_COLORS = {
  circle_office_warehouse: "bg-blue-100 text-blue-700",
  hq: "bg-purple-100 text-purple-700",
  depot: "bg-green-100 text-green-700",
  sub_depot: "bg-orange-100 text-orange-700"
};

export default function LocationMasterPage() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [circleFilter, setCircleFilter] = useState("");
  const [circles, setCircles] = useState([]);
  
  // Modal states
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [creating, setCreating] = useState(false);
  const [capturingGps, setCapturingGps] = useState(false);
  
  const [form, setForm] = useState({
    code: "",
    name: "",
    location_type: "depot",
    circle_name: "",
    package_name: "",
    address: "",
    gps_lat: "",
    gps_lng: "",
    contact_phone: ""
  });

  const defaultForm = {
    code: "",
    name: "",
    location_type: "depot",
    circle_name: "",
    package_name: "",
    address: "",
    gps_lat: "",
    gps_lng: "",
    contact_phone: ""
  };

  const isAdmin = ["admin", "ci"].includes(user?.role);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.append("location_type", typeFilter);
      if (circleFilter) params.append("circle_name", circleFilter);
      
      const res = await api.get(`/locations?${params.toString()}`);
      setLocations(res.data.items || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error("Error fetching locations:", err);
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, circleFilter]);

  // Fetch circles for filter
  const fetchCircles = useCallback(async () => {
    try {
      const res = await api.get("/hierarchy/circles");
      setCircles(res.data || []);
    } catch (err) {
      console.error("Error fetching circles:", err);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchCircles();
  }, [fetchLocations, fetchCircles]);

  // Filter locations by search
  const filteredLocations = locations.filter(loc => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      loc.code?.toLowerCase().includes(s) ||
      loc.name?.toLowerCase().includes(s) ||
      loc.circle_name?.toLowerCase().includes(s) ||
      loc.address?.toLowerCase().includes(s)
    );
  });

  // Capture current GPS
  const captureGps = () => {
    setCapturingGps(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setForm({
            ...form,
            gps_lat: position.coords.latitude.toFixed(6),
            gps_lng: position.coords.longitude.toFixed(6)
          });
          setCapturingGps(false);
          toast.success("GPS location captured");
        },
        (error) => {
          console.error("GPS Error:", error);
          setCapturingGps(false);
          toast.error("Failed to capture GPS. Please enter manually.");
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setCapturingGps(false);
      toast.error("Geolocation not supported");
    }
  };

  // Create location
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name || !form.location_type) {
      toast.error("Please fill required fields");
      return;
    }
    setCreating(true);
    try {
      const payload = { ...form };
      if (payload.gps_lat) payload.gps_lat = parseFloat(payload.gps_lat);
      if (payload.gps_lng) payload.gps_lng = parseFloat(payload.gps_lng);
      
      await api.post("/locations", payload);
      toast.success("Location created successfully");
      setShowCreate(false);
      setForm(defaultForm);
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create location");
    } finally {
      setCreating(false);
    }
  };

  // Update location
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Location name is required"); return; }
    setCreating(true);
    try {
      const payload = {
        name: form.name,
        address: form.address,
        gps_lat: form.gps_lat ? parseFloat(form.gps_lat) : null,
        gps_lng: form.gps_lng ? parseFloat(form.gps_lng) : null,
        contact_phone: form.contact_phone
      };
      
      await api.put(`/locations/${editLocation.id}`, payload);
      toast.success("Location updated successfully");
      setShowEdit(false);
      setEditLocation(null);
      setForm(defaultForm);
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update location");
    } finally {
      setCreating(false);
    }
  };

  // Delete location
  const handleDelete = async (locationId) => {
    if (!confirm("Are you sure you want to delete this location?")) return;
    try {
      await api.delete(`/locations/${locationId}`);
      toast.success("Location deleted");
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete location");
    }
  };

  // Open edit modal
  const openEditModal = (loc) => {
    setEditLocation(loc);
    setForm({
      code: loc.code || "",
      name: loc.name || "",
      location_type: loc.location_type || "depot",
      circle_name: loc.circle_name || "",
      package_name: loc.package_name || "",
      address: loc.address || "",
      gps_lat: loc.gps_lat?.toString() || "",
      gps_lng: loc.gps_lng?.toString() || "",
      contact_phone: loc.contact_phone || ""
    });
    setShowEdit(true);
  };

  // Export to CSV
  const exportToCsv = () => {
    const headers = ["Code", "Name", "Type", "Circle", "Address", "Latitude", "Longitude", "Status"];
    const rows = locations.map(loc => [
      loc.code,
      loc.name,
      loc.location_type,
      loc.circle_name || "",
      loc.address || "",
      loc.gps_lat || "",
      loc.gps_lng || "",
      loc.status || "active"
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `locations_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported to CSV");
  };

  // Bulk import handler
  const handleBulkImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split("\n").filter(line => line.trim());
        const headers = lines[0].split(",").map(h => h.replace(/"/g, "").trim().toLowerCase());
        
        const locations = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(",").map(v => v.replace(/"/g, "").trim());
          const loc = {};
          headers.forEach((h, idx) => {
            if (h === "code") loc.code = values[idx];
            else if (h === "name") loc.name = values[idx];
            else if (h === "type" || h === "location_type") loc.location_type = values[idx];
            else if (h === "circle" || h === "circle_name") loc.circle_name = values[idx];
            else if (h === "address") loc.address = values[idx];
            else if (h === "latitude" || h === "gps_lat") loc.gps_lat = values[idx];
            else if (h === "longitude" || h === "gps_lng") loc.gps_lng = values[idx];
          });
          if (loc.code) locations.push(loc);
        }

        const res = await api.post("/locations/bulk-import", locations);
        toast.success(res.data.message);
        if (res.data.errors?.length) {
          res.data.errors.forEach(err => toast.warning(err));
        }
        fetchLocations();
        setShowImport(false);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to import CSV");
      }
    };
    reader.readAsText(file);
  };

  const getTypeInfo = (type) => LOCATION_TYPES.find(t => t.value === type) || LOCATION_TYPES[2];

  return (
    <div className="space-y-6" data-testid="location-master-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Location Master</h1>
          <p className="text-sm text-slate-500">Manage offices, warehouses, depots with GPS coordinates</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button
                onClick={() => setShowImport(true)}
                className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </button>
              <button
                onClick={exportToCsv}
                className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                data-testid="add-location-btn"
              >
                <Plus className="w-4 h-4" />
                Add Location
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {LOCATION_TYPES.map(type => {
          const TypeIcon = type.icon;
          const count = summary[type.value] || 0;
          return (
            <div 
              key={type.value}
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                typeFilter === type.value ? "border-orange-500 bg-orange-50" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
              onClick={() => setTypeFilter(typeFilter === type.value ? "" : type.value)}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${type.color} text-white`}>
                  <TypeIcon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-800">{count}</div>
                  <div className="text-xs text-slate-500">{type.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by code, name, address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            data-testid="search-input"
          />
        </div>
        <select
          value={circleFilter}
          onChange={(e) => setCircleFilter(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
        >
          <option value="">All Circles</option>
          {circles.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(typeFilter || circleFilter || search) && (
          <button
            onClick={() => { setTypeFilter(""); setCircleFilter(""); setSearch(""); }}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Locations Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4">Code</th>
              <th className="text-left py-3 px-4">Name</th>
              <th className="text-left py-3 px-4">Type</th>
              <th className="text-left py-3 px-4">Circle</th>
              <th className="text-left py-3 px-4">Address</th>
              <th className="text-left py-3 px-4">GPS</th>
              <th className="text-left py-3 px-4">Status</th>
              {isAdmin && <th className="text-left py-3 px-4">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {[...Array(8)].map((_, j) => (
                    <td key={j} className="py-3 px-4">
                      <div className="h-4 bg-slate-100 rounded animate-pulse"></div>
                    </td>
                  ))}
                </tr>
              ))
            ) : filteredLocations.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12 text-slate-400">
                  <MapPin className="w-10 h-10 mx-auto mb-2" />
                  <p>No locations found</p>
                  {isAdmin && <p className="text-xs mt-1">Click "Add Location" to create one</p>}
                </td>
              </tr>
            ) : filteredLocations.map(loc => {
              const typeInfo = getTypeInfo(loc.location_type);
              return (
                <tr key={loc.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <span className="font-mono text-xs font-bold text-slate-700">{loc.code}</span>
                  </td>
                  <td className="py-3 px-4 font-medium text-slate-800">{loc.name}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${TYPE_COLORS[loc.location_type] || "bg-slate-100 text-slate-600"}`}>
                      {typeInfo.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-600">{loc.circle_name || "—"}</td>
                  <td className="py-3 px-4 text-slate-500 text-xs max-w-[200px] truncate">{loc.address || "—"}</td>
                  <td className="py-3 px-4">
                    {loc.gps_lat && loc.gps_lng ? (
                      <span className="flex items-center gap-1 text-green-600 text-xs">
                        <MapPin className="w-3 h-3" />
                        {parseFloat(loc.gps_lat).toFixed(4)}, {parseFloat(loc.gps_lng).toFixed(4)}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-500 text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        Not Set
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] font-bold uppercase ${loc.status === "active" ? "text-green-600" : "text-red-500"}`}>
                      {loc.status || "active"}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditModal(loc)}
                          className="p-1 text-blue-500 hover:text-blue-700"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(loc.id)}
                          className="p-1 text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-semibold text-slate-800">Add New Location</h3>
              <button onClick={() => { setShowCreate(false); setForm(defaultForm); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="LOC-VNS-001"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    required
                    data-testid="loc-code-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                  <select
                    value={form.location_type}
                    onChange={(e) => setForm({ ...form, location_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    required
                    data-testid="loc-type-select"
                  >
                    {LOCATION_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Varanasi Central Office & Warehouse"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  required
                  data-testid="loc-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Circle</label>
                  <select
                    value={form.circle_name}
                    onChange={(e) => setForm({ ...form, circle_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="">Select Circle</option>
                    {circles.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
                  <input
                    type="tel"
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    placeholder="9876543210"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Full address with landmark..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              
              {/* GPS Section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-orange-500" />
                    GPS Coordinates
                  </label>
                  <button
                    type="button"
                    onClick={captureGps}
                    disabled={capturingGps}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-xs font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
                  >
                    {capturingGps ? <><Loader2 className="w-3 h-3 animate-spin" /> Capturing...</> : <><Navigation className="w-3 h-3" /> Capture Current GPS</>}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      value={form.gps_lat}
                      onChange={(e) => setForm({ ...form, gps_lat: e.target.value })}
                      placeholder="25.3176"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      data-testid="loc-lat-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      value={form.gps_lng}
                      onChange={(e) => setForm({ ...form, gps_lng: e.target.value })}
                      placeholder="82.9739"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      data-testid="loc-lng-input"
                    />
                  </div>
                </div>
                {form.gps_lat && form.gps_lng && (
                  <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-700 flex items-center gap-2">
                    <Check className="w-3 h-3" />
                    GPS Set: {parseFloat(form.gps_lat).toFixed(6)}, {parseFloat(form.gps_lng).toFixed(6)}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); setForm(defaultForm); }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="create-loc-btn"
                >
                  {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                  Add Location
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && editLocation && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-semibold text-slate-800">Edit Location</h3>
              <button onClick={() => { setShowEdit(false); setEditLocation(null); setForm(defaultForm); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-4 space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-xs text-slate-500">Code</div>
                <div className="font-mono font-bold text-slate-800">{editLocation.code}</div>
                <div className="text-xs text-slate-500 mt-2">Type</div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${TYPE_COLORS[editLocation.location_type] || "bg-slate-100"}`}>
                  {getTypeInfo(editLocation.location_type).label}
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              
              {/* GPS Section */}
              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-orange-500" />
                    GPS Coordinates
                  </label>
                  <button
                    type="button"
                    onClick={captureGps}
                    disabled={capturingGps}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-xs font-medium hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
                  >
                    {capturingGps ? <><Loader2 className="w-3 h-3 animate-spin" /> Capturing...</> : <><Navigation className="w-3 h-3" /> Capture Current GPS</>}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                    <input
                      type="number"
                      step="any"
                      value={form.gps_lat}
                      onChange={(e) => setForm({ ...form, gps_lat: e.target.value })}
                      placeholder="25.3176"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                    <input
                      type="number"
                      step="any"
                      value={form.gps_lng}
                      onChange={(e) => setForm({ ...form, gps_lng: e.target.value })}
                      placeholder="82.9739"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                </div>
                {form.gps_lat && form.gps_lng && (
                  <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-700 flex items-center gap-2">
                    <Check className="w-3 h-3" />
                    GPS: {parseFloat(form.gps_lat).toFixed(6)}, {parseFloat(form.gps_lng).toFixed(6)}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowEdit(false); setEditLocation(null); setForm(defaultForm); }}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-semibold text-slate-800">Import Locations from CSV</h3>
              <button onClick={() => setShowImport(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium text-slate-700 mb-2">CSV Format:</p>
                <code className="text-xs text-slate-600 block">
                  code,name,type,circle,address,latitude,longitude
                </code>
                <p className="text-xs text-slate-500 mt-2">
                  Types: circle_office_warehouse, hq, depot, sub_depot
                </p>
              </div>
              <div>
                <label className="block w-full">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleBulkImport}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
                  />
                </label>
              </div>
              <button
                onClick={() => setShowImport(false)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
